import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
} from '@nextui-org/react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/modal';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ResourceInfo, SectionInfo } from '../types';
import {
  fetchAllResources,
  deleteResource,
  createSection,
  updateSection,
  deleteSection,
} from '../services/resourceService';
import AddResourceModal from './AddResourceModal';
import EditResourceModal from './EditResourceModal';
import { useAuth0 } from '@auth0/auth0-react';

const ResourcesManagement: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [resources, setResources] = useState<ResourceInfo[]>([]);
  const [sections, setSections] = useState<SectionInfo[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<ResourceInfo | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingSectionName, setEditingSectionName] = useState('');

  const isAdmin =
    user && (user['https://api.airlock/roles'] || []).includes('admin');

  const loadData = async () => {
    try {
      const token = await getAccessTokenSilently();
      const data = await fetchAllResources(token);
      setResources(data.resources || []);
      setSections(data.sections || []);
      setIsLoaded(true);
    } catch (err: any) {
      setError(err.message);
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      navigate('/resources');
      return;
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleDeleteResource = async (id: string) => {
    if (!window.confirm('Delete this resource?')) return;
    try {
      const token = await getAccessTokenSilently();
      await deleteResource(token, id);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddSection = async () => {
    if (!newSectionName.trim()) return;
    try {
      const token = await getAccessTokenSilently();
      await createSection(token, {
        name: newSectionName.trim(),
        displayOrder: sections.length,
      });
      setNewSectionName('');
      setIsAddSectionOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateSection = async (id: string) => {
    if (!editingSectionName.trim()) return;
    try {
      const token = await getAccessTokenSilently();
      await updateSection(token, id, { name: editingSectionName.trim() });
      setEditingSectionId(null);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteSection = async (id: string) => {
    try {
      const token = await getAccessTokenSilently();
      await deleteSection(token, id);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const sortedSections = [...sections].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const getResourcesForSection = (sectionName: string) =>
    resources
      .filter((r) => r.section === sectionName)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Manage Resources
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Add, edit, and organize partner resources
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="bordered"
            className="border-slate-300 text-slate-600"
            radius="sm"
            onClick={() => navigate('/resources')}
          >
            Back to Resources
          </Button>
          <Button
            variant="bordered"
            className="border-slate-300 text-slate-600"
            radius="sm"
            onClick={() => setIsAddSectionOpen(true)}
          >
            + Section
          </Button>
          <Button
            color="primary"
            className="bg-airlock-purple hover:bg-airlock-deep text-white font-medium"
            radius="sm"
            onClick={() => setIsAddOpen(true)}
          >
            + Resource
          </Button>
        </div>
      </div>

      {error && (
        <Card className="mb-6 bg-red-50 border border-red-200 shadow-none">
          <CardBody className="p-4">
            <p className="text-sm text-red-700">{error}</p>
            <Button
              size="sm"
              variant="light"
              className="text-red-600 mt-1"
              onClick={() => setError(null)}
            >
              Dismiss
            </Button>
          </CardBody>
        </Card>
      )}

      {sortedSections.map((section) => {
        const sectionResources = getResourcesForSection(section.name);
        return (
          <div key={section._id} className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              {editingSectionId === section._id ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    size="sm"
                    variant="bordered"
                    radius="sm"
                    value={editingSectionName}
                    onChange={(e) => setEditingSectionName(e.target.value)}
                    className="max-w-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUpdateSection(section._id);
                      if (e.key === 'Escape') setEditingSectionId(null);
                    }}
                  />
                  <Button
                    size="sm"
                    color="primary"
                    radius="sm"
                    className="bg-airlock-purple"
                    onClick={() => handleUpdateSection(section._id)}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    radius="sm"
                    onClick={() => setEditingSectionId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
                    {section.name}
                  </h2>
                  <Chip size="sm" variant="flat" className="text-xs bg-slate-100 text-slate-400">
                    {sectionResources.length}
                  </Chip>
                  <button
                    onClick={() => {
                      setEditingSectionId(section._id);
                      setEditingSectionName(section.name);
                    }}
                    className="text-xs text-slate-400 hover:text-slate-600"
                  >
                    rename
                  </button>
                  {sectionResources.length === 0 && (
                    <button
                      onClick={() => handleDeleteSection(section._id)}
                      className="text-xs text-red-400 hover:text-red-600"
                    >
                      delete
                    </button>
                  )}
                </>
              )}
            </div>

            {sectionResources.length > 0 ? (
              <div className="space-y-2">
                {sectionResources.map((resource) => (
                  <Card
                    key={resource._id}
                    className={`border shadow-none ${
                      resource.active
                        ? 'bg-white border-slate-200'
                        : 'bg-slate-50 border-slate-200 opacity-60'
                    }`}
                  >
                    <CardBody className="p-3">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-800 truncate">
                              {resource.title}
                            </p>
                            <Chip
                              size="sm"
                              variant="flat"
                              className="text-xs bg-slate-100 text-slate-500"
                            >
                              {resource.resourceType === 'link' ? 'LINK' : `.${resource.fileType}`}
                            </Chip>
                            {!resource.active && (
                              <Chip size="sm" variant="flat" color="warning" className="text-xs">
                                Inactive
                              </Chip>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 truncate">
                            {resource.resourceType === 'link' ? resource.url : resource.filename}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="light"
                            className="text-slate-500"
                            onClick={() => {
                              setEditingResource(resource);
                              setIsEditOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="light"
                            className="text-red-500"
                            onClick={() => handleDeleteResource(resource._id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">
                No resources in this section
              </p>
            )}
          </div>
        );
      })}

      {isLoaded && sections.length === 0 && (
        <Card className="bg-white border border-slate-200 shadow-none">
          <CardBody className="p-8 text-center">
            <p className="text-slate-500 text-sm mb-3">
              No sections yet. Create a section to start organizing resources.
            </p>
            <Button
              color="primary"
              className="bg-airlock-purple"
              radius="sm"
              onClick={() => setIsAddSectionOpen(true)}
            >
              Create First Section
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Add Resource Modal */}
      <AddResourceModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        sections={sections}
        onSaved={loadData}
      />

      {/* Edit Resource Modal */}
      <EditResourceModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setEditingResource(null);
        }}
        resource={editingResource}
        sections={sections}
        onSaved={loadData}
      />

      {/* Add Section Modal */}
      <Modal isOpen={isAddSectionOpen} onClose={() => setIsAddSectionOpen(false)}>
        <ModalContent>
          <ModalHeader className="pb-2">
            <h3 className="text-lg font-bold text-slate-800">Add Section</h3>
          </ModalHeader>
          <ModalBody>
            <Input
              label="Section Name"
              variant="bordered"
              radius="sm"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSection()}
              classNames={{ label: 'text-slate-500 text-xs' }}
            />
          </ModalBody>
          <ModalFooter className="pt-2">
            <Button
              variant="bordered"
              onClick={() => setIsAddSectionOpen(false)}
              radius="sm"
              className="border-slate-300 text-slate-600"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAddSection}
              radius="sm"
              className="bg-airlock-purple font-medium"
            >
              Create Section
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ResourcesManagement;
